import { DSText } from '@engenharia/ds-react';
import styled from 'styled-components';
import colors from '../../../../styles/theme/colors';
import { sizes } from '../../../../styles/theme/devices';
import spaces from '../../../../styles/theme/spaces';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${spaces.xs};
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${colors.gray[50]};
  border: 1px solid ${colors.gray[100]};
  max-width: 100%;
`;

const BadgeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tone }) => {
    if ($tone === 'agency') return '#EDE7F6';
    if ($tone === 'franchise') return '#E3F2FF';
    return colors.gray[100];
  }};
  color: ${({ $tone }) => {
    if ($tone === 'agency') return '#7E57C2';
    if ($tone === 'franchise') return colors.secondary.main;
    return colors.gray[600];
  }};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BadgeText = styled(DSText)`
  color: ${colors.gray[700]};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;

  strong {
    font-weight: 600;
    color: ${colors.gray[800]};
  }
`;

const Subtitle = styled(DSText)`
  color: ${colors.gray[500]};
  font-size: 13px;
  font-weight: 400;
  text-align: center;
  line-height: 1.45;
  margin: -4px 0 ${spaces.micro};
  max-width: 420px;
`;

const OperationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;

  @media (min-width: ${sizes.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }
`;

const ScopeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;

  @media (min-width: ${sizes.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ScaleRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;

  @media (min-width: ${sizes.tablet}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }
`;

const SelectRadio = styled.div`
  flex-shrink: 0;
  height: 18px;
  width: 18px;
  border-radius: 50%;
  border: 2px solid ${colors.gray[100]};
  position: relative;
  background: ${colors.default.white};

  ${({ $checked }) =>
    $checked &&
    `
    border-color: ${colors.primary.main};

    &::before {
      content: '';
      width: 8px;
      height: 8px;
      background: ${colors.primary.main};
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
    }
  `}
`;

const OperationOption = styled.button`
  align-items: flex-start;
  background: ${colors.default.white};
  border: 1.5px solid ${({ $checked }) => ($checked ? colors.primary.main : colors.gray[100])};
  border-radius: 12px;
  cursor: pointer;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  padding: 14px;
  text-align: left;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
  width: 100%;
  min-height: 96px;

  ${({ $checked }) =>
    $checked &&
    `
    background: ${colors.orange[50]};
  `}

  &:hover {
    border-color: ${colors.primary.main};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

const OptionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tone }) => {
    if ($tone === 'few') return colors.orange[50];
    if ($tone === 'agency') return '#EDE7F6';
    if ($tone === 'franchise') return '#E3F2FF';
    return colors.gray[50];
  }};
  color: ${({ $tone }) => {
    if ($tone === 'few') return colors.primary.main;
    if ($tone === 'agency') return '#7E57C2';
    if ($tone === 'franchise') return colors.secondary.main;
    return colors.gray[600];
  }};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const OptionCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const OptionTitle = styled(DSText)`
  color: ${colors.gray[800]};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
`;

const OptionDescription = styled(DSText)`
  color: ${colors.gray[500]};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
`;

const ScopeOption = styled.button`
  align-items: center;
  background: ${colors.default.white};
  border: 1.5px solid ${({ $checked }) => ($checked ? colors.primary.main : colors.gray[100])};
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 14px;
  text-align: center;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
  width: 100%;
  min-height: 148px;

  ${({ $checked }) =>
    $checked &&
    `
    background: ${colors.orange[50]};
  `}

  &:hover {
    border-color: ${colors.primary.main};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

const ScopeIcon = styled(OptionIcon)`
  width: 48px;
  height: 48px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ScaleOption = styled.button`
  align-items: center;
  background: ${colors.default.white};
  border: 1.5px solid ${({ $checked }) => ($checked ? colors.primary.main : colors.gray[100])};
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  padding: 14px 8px;
  text-align: center;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
  width: 100%;
  min-height: 110px;

  ${({ $checked }) =>
    $checked &&
    `
    background: ${colors.orange[50]};
  `}

  &:hover {
    border-color: ${colors.primary.main};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

const ScaleValue = styled(DSText)`
  color: ${colors.gray[800]};
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;

  @media (min-width: ${sizes.tablet}) {
    font-size: 22px;
  }
`;

const ScaleUnit = styled(DSText)`
  color: ${colors.gray[500]};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
`;

export {
  Root,
  Badge,
  BadgeIcon,
  BadgeText,
  Subtitle,
  OperationGrid,
  ScopeGrid,
  ScaleRow,
  SelectRadio,
  OperationOption,
  OptionIcon,
  OptionCopy,
  OptionTitle,
  OptionDescription,
  ScopeOption,
  ScopeIcon,
  ScaleOption,
  ScaleValue,
  ScaleUnit,
};
